import { useFormik } from 'formik';
import { useToast } from '@chakra-ui/react';
import { useAddGroupMembers } from 'features/user-groups/api';
import { MemberOption } from 'features/user-groups/components/AddMembersDropdown/types';
import {
  addMembersValidationSchema,
  getAddedToGroupSuccess,
  getAddedToGroupError,
  getPartialAddToGroupWarning,
} from 'features/user-groups/components/CreateTeamDrawer/utils';
import { useDrawerStateDispatch } from 'features/main/context';
import { closeDrawer } from 'features/main/context/drawer/actionCreators';
import { useOrgGroupMembersList } from '../api/useOrgGroupMembersList';

// Recursive function to add members in batches of 5
const addMembersRecursively = async (
  addGroupMembersMutation: any,
  groupId: string,
  memberIds: string[],
  batchIndex: number = 0,
  failedCount: number = 0,
): Promise<number> => {
  // Base case: no more members to process
  if (memberIds.length === 0) return failedCount;

  const batch = memberIds.slice(0, 5);
  const remaining = memberIds.slice(5);

  let batchFailed = 0;
  try {
    await addGroupMembersMutation.mutateAsync({
      groupId,
      members: batch,
    });
  } catch (error) {
    console.error('Batch %d failed:', batchIndex + 1, error);
    batchFailed = batch.length;
  }

  // Continue with remaining members regardless of batch outcome
  return addMembersRecursively(
    addGroupMembersMutation,
    groupId,
    remaining,
    batchIndex + 1,
    failedCount + batchFailed,
  );
};

export const useAddMembersToGroups = () => {
  const toast = useToast();
  const [{ data: groupIdData }, drawerDispatch] = useDrawerStateDispatch();
  const groupId =
    typeof groupIdData?.groupId === 'string' ? groupIdData.groupId : '';
  const addGroupMembersMutation = useAddGroupMembers();
  const { data: { data: orgGroupMembers = [] } = {} } =
    useOrgGroupMembersList(groupId, { enabled: !!groupId }) || {};

  const handleCloseDrawer = () => {
    formik.resetForm();
    drawerDispatch(closeDrawer());
  };

  // Formik setup
  const formik = useFormik({
    initialValues: { members: [] },
    validationSchema: addMembersValidationSchema,
    validateOnMount: true,
    onSubmit: async () => {
      if (!groupId) return;
      if (formik.values.members.length > 0) {
        const memberIds = formik.values.members.map(({ value }) => value);
        const failedCount = await addMembersRecursively(
          addGroupMembersMutation,
          groupId,
          memberIds,
        );

        const totalCount = formik.values.members.length;
        const successCount = totalCount - failedCount;

        if (failedCount > 0 && successCount > 0) {
          toast({
            description: getPartialAddToGroupWarning(successCount, totalCount),
            status: 'warning',
          });
          handleCloseDrawer();
        } else if (failedCount > 0) {
          toast({
            description: getAddedToGroupError(failedCount),
            status: 'error',
          });
        } else {
          toast({
            description: getAddedToGroupSuccess(totalCount),
            status: 'success',
          });
          handleCloseDrawer();
        }
      }
    },
  });

  // Validate immediately on empty dropdown
  if (
    Array.isArray(formik.values.members) &&
    formik.values.members.length === 0 &&
    !formik.touched.members
  ) {
    formik.setFieldTouched('members', true, false);
  }

  const handleMembersSelect = (members: MemberOption[]) => {
    const existingIds = new Set(orgGroupMembers.map((member) => member.id));
    const highlightedMembers = members.map((member) => ({
      ...member,
      highlight: existingIds.has(member.value),
    }));
    formik.setFieldValue('members', highlightedMembers);
  };

  return {
    // Formik values (matching the exact interface)
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    handleChange: formik.handleChange,
    handleSubmit: formik.handleSubmit,
    handleBlur: formik.handleBlur,
    isSubmitting: formik.isSubmitting || addGroupMembersMutation.isLoading,
    resetForm: formik.resetForm,

    // Additional functionality
    handleMembersSelect,
    handleCloseDrawer,
  };
};
